import {
  SuperSocialCreatorResponseDto,
} from '@/app/common/general-types/creators.dto';
import { FilterCreatorsDto } from '@/app/common/general-types/filters.dto';

export type CreatorStatsPlatform = 'tiktok' | 'instagram' | 'youtube' | 'snapchat';

export interface CreatorPlatformFollowerCounts {
  tiktok?: number | null;
  instagram?: number | null;
  youtube?: number | null;
  snapchat?: number | null;
}

export interface CreatorPlatformStatMetric {
  key: 'followers' | 'averageViews' | 'averageEngagementRate' | 'totalViews' | 'averageLikes' | 'totalLikes';
  label: string;
  value: number;
  format: 'count' | 'percent';
}

export interface CreatorPlatformStatRow {
  platform: CreatorStatsPlatform;
  label: string;
  iconSrc: string;
  averageViews: number;
  averageEngagementRate: number;
  extraMetrics: CreatorPlatformStatMetric[];
  metrics: CreatorPlatformStatMetric[];
}

type SsAverages = NonNullable<SuperSocialCreatorResponseDto['tiktokAverages']>;

const PLATFORM_META: {
  platform: CreatorStatsPlatform;
  label: string;
  iconSrc: string;
  averagesKey: 'tiktokAverages' | 'instagramAverages' | 'youtubeAverages' | null;
  followerKey: keyof CreatorPlatformFollowerCounts;
  followerLabel: string;
  followerFrom: string;
  followerTo: string;
  engagementFrom: string | null;
  viewsFrom: string | null;
  viewsTo: string | null;
  extraMetricKeys: {
    key: Extract<CreatorPlatformStatMetric['key'], 'totalViews' | 'averageLikes' | 'totalLikes'>;
    label: string;
    from: string;
    to: string;
  }[];
}[] = [
  {
    platform: 'tiktok',
    label: 'TikTok',
    iconSrc: 'assets/social-icon/tik-tok-transparent.svg',
    averagesKey: 'tiktokAverages',
    followerKey: 'tiktok',
    followerLabel: 'followers',
    followerFrom: 'tikTokFollowersFrom',
    followerTo: 'tikTokFollowersTo',
    engagementFrom: 'tikTokAverageEngagementFrom',
    viewsFrom: 'tikTokAverageViewsFrom',
    viewsTo: 'tikTokAverageViewsTo',
    extraMetricKeys: [
      { key: 'totalViews', label: 'total views', from: 'tikTokTotalViewsFrom', to: 'tikTokTotalViewsTo' },
      { key: 'averageLikes', label: 'avg likes', from: 'tikTokAverageLikesFrom', to: 'tikTokAverageLikesTo' },
      { key: 'totalLikes', label: 'likes', from: 'tikTokTotalLikesFrom', to: 'tikTokTotalLikesTo' },
    ],
  },
  {
    platform: 'instagram',
    label: 'Instagram',
    iconSrc: 'assets/social-icon/instagram-logo.png',
    averagesKey: 'instagramAverages',
    followerKey: 'instagram',
    followerLabel: 'followers',
    followerFrom: 'instagramFollowersFrom',
    followerTo: 'instagramFollowersTo',
    engagementFrom: 'instagramAverageEngagementFrom',
    viewsFrom: 'instagramAverageViewsFrom',
    viewsTo: 'instagramAverageViewsTo',
    extraMetricKeys: [
      { key: 'totalViews', label: 'total views', from: 'instagramTotalViewsFrom', to: 'instagramTotalViewsTo' },
      { key: 'averageLikes', label: 'avg likes', from: 'instagramAverageLikesFrom', to: 'instagramAverageLikesTo' },
      { key: 'totalLikes', label: 'likes', from: 'instagramTotalLikesFrom', to: 'instagramTotalLikesTo' },
    ],
  },
  {
    platform: 'youtube',
    label: 'YouTube',
    iconSrc: 'assets/social-icon/youtube_shorts.webp',
    averagesKey: 'youtubeAverages',
    followerKey: 'youtube',
    followerLabel: 'subscribers',
    followerFrom: 'youtubeSubscribersFrom',
    followerTo: 'youtubeSubscribersTo',
    engagementFrom: 'youtubeAverageEngagementFrom',
    viewsFrom: 'youtubeAverageViewsFrom',
    viewsTo: 'youtubeAverageViewsTo',
    extraMetricKeys: [
      { key: 'totalViews', label: 'total views', from: 'youtubeTotalViewsFrom', to: 'youtubeTotalViewsTo' },
      { key: 'averageLikes', label: 'avg likes', from: 'youtubeAverageLikesFrom', to: 'youtubeAverageLikesTo' },
      { key: 'totalLikes', label: 'likes', from: 'youtubeTotalLikesFrom', to: 'youtubeTotalLikesTo' },
    ],
  },
  {
    platform: 'snapchat',
    label: 'Snapchat',
    iconSrc: 'assets/social-icon/snapchat-logo.svg',
    averagesKey: null,
    followerKey: 'snapchat',
    followerLabel: 'followers',
    followerFrom: 'snapchatFollowersFrom',
    followerTo: 'snapchatFollowersTo',
    engagementFrom: null,
    viewsFrom: null,
    viewsTo: null,
    extraMetricKeys: [],
  },
];

function isFilterValueSet(value: unknown): boolean {
  if (value == null) {
    return false;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }
  return false;
}

function platformFilterKeys(meta: (typeof PLATFORM_META)[number]): string[] {
  return [
    meta.followerFrom,
    meta.followerTo,
    meta.engagementFrom,
    meta.viewsFrom,
    meta.viewsTo,
    ...meta.extraMetricKeys.flatMap((metric) => [metric.from, metric.to]),
  ].filter((key): key is string => key != null);
}

function isRangeFiltered(filters: FilterCreatorsDto, from?: string | null, to?: string | null): boolean {
  return (!!from && isFilterValueSet(filters[from])) || (!!to && isFilterValueSet(filters[to]));
}

export function creatorStatsPlatformsFromFilters(
  filters?: FilterCreatorsDto | null,
): CreatorStatsPlatform[] {
  if (!filters) {
    return [];
  }

  return PLATFORM_META
    .filter((meta) => platformFilterKeys(meta).some((key) => isFilterValueSet(filters[key])))
    .map((meta) => meta.platform);
}

export function listCreatorPlatformStats(
  ssCreator?: SuperSocialCreatorResponseDto | null,
  options?: {
    filters?: FilterCreatorsDto | null;
    followerCounts?: CreatorPlatformFollowerCounts;
  },
): CreatorPlatformStatRow[] {
  const filters = options?.filters ?? null;
  const followerCounts = options?.followerCounts;
  if (!ssCreator && !followerCounts) {
    return [];
  }

  const filteredPlatforms = creatorStatsPlatformsFromFilters(filters);
  const allowed = filteredPlatforms.length > 0 ? new Set(filteredPlatforms) : null;
  const rows: CreatorPlatformStatRow[] = [];

  for (const meta of PLATFORM_META) {
    if (allowed && !allowed.has(meta.platform)) {
      continue;
    }

    const averages = meta.averagesKey && ssCreator ? ssCreator[meta.averagesKey] : undefined;
    const averageViews = averages?.averageViews ?? 0;
    const averageEngagementRate = averages?.averageEngagementRate ?? 0;
    const metrics = allowed
      ? metricsForAppliedFilters(meta, averages, filters, followerCounts)
      : defaultMetrics(averageViews, averageEngagementRate);
    const extraMetrics = metrics.filter((metric) =>
      metric.key === 'totalViews' || metric.key === 'averageLikes' || metric.key === 'totalLikes',
    );

    if (metrics.length === 0) {
      continue;
    }

    rows.push({
      platform: meta.platform,
      label: meta.label,
      iconSrc: meta.iconSrc,
      averageViews,
      averageEngagementRate,
      extraMetrics,
      metrics,
    });
  }
  return rows;
}

function defaultMetrics(averageViews: number, averageEngagementRate: number): CreatorPlatformStatMetric[] {
  const metrics: CreatorPlatformStatMetric[] = [];
  if (averageViews > 0) {
    metrics.push({
      key: 'averageViews',
      label: 'views',
      value: averageViews,
      format: 'count',
    });
  }
  if (averageEngagementRate > 0) {
    metrics.push({
      key: 'averageEngagementRate',
      label: '',
      value: averageEngagementRate,
      format: 'percent',
    });
  }
  return metrics;
}

function metricsForAppliedFilters(
  meta: (typeof PLATFORM_META)[number],
  averages: SsAverages | undefined,
  filters: FilterCreatorsDto | null,
  followerCounts?: CreatorPlatformFollowerCounts,
): CreatorPlatformStatMetric[] {
  if (!filters) {
    return [];
  }

  const metrics: CreatorPlatformStatMetric[] = [];
  const followers = followerCounts?.[meta.followerKey] ?? 0;

  if (isRangeFiltered(filters, meta.followerFrom, meta.followerTo) && followers > 0) {
    metrics.push({
      key: 'followers',
      label: meta.followerLabel,
      value: followers,
      format: 'count',
    });
  }

  if (meta.engagementFrom && isRangeFiltered(filters, meta.engagementFrom, null)) {
    const averageEngagementRate = averages?.averageEngagementRate ?? 0;
    if (averageEngagementRate > 0) {
      metrics.push({
        key: 'averageEngagementRate',
        label: '',
        value: averageEngagementRate,
        format: 'percent',
      });
    }
  }

  if (isRangeFiltered(filters, meta.viewsFrom, meta.viewsTo)) {
    const averageViews = averages?.averageViews ?? 0;
    if (averageViews > 0) {
      metrics.push({
        key: 'averageViews',
        label: 'views',
        value: averageViews,
        format: 'count',
      });
    }
  }

  for (const metric of meta.extraMetricKeys) {
    if (!isRangeFiltered(filters, metric.from, metric.to)) {
      continue;
    }
    const value = averages?.[metric.key] ?? 0;
    if (value <= 0) {
      continue;
    }
    metrics.push({
      key: metric.key,
      label: metric.label,
      value,
      format: 'count',
    });
  }

  return metrics;
}
